import { useEffect, useState } from "react";

import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { Graph } from "@/components/doc/right-sidebar/graph-preview/Graph";
import { GraphPreviewSelection } from "@/components/doc/right-sidebar/graph-preview/GraphPreviewSelection";
import { useGraphPreview } from "@/components/doc/right-sidebar/graph-preview/useGraphPreview";
import {
  canGraphExpressions,
  EXPRESSION_COUNT_LIMIT,
  getSelectedExpressions,
} from "@/components/graph/utils";
import { ActionsDisclosure } from "@/components/sidebar/ActionsDisclosure";
import { trackEvent } from "@/utils/trackEvent";
import { twMerge } from "@/utils/twMerge";

import type { DocumentModel } from "@/models/DocumentModel";
import type { NodeModel } from "@/models/nodes/types";

const GraphPreviewPanelComponent = observer(function GraphPreviewPanelComponent({
  doc,
  expressions,
  hidden,
}: {
  doc: DocumentModel;
  expressions: NodeModel[][];
  hidden: boolean;
}) {
  const {
    ref,
    api,
    expression,
    commandRunner: { evalStatus },
  } = useGraphPreview(expressions, { width: 250, height: 200 }, doc);
  const t = useTranslations("sidebar.activeNode");
  const hideGraph = !expression || evalStatus !== "success" || hidden;

  return (
    <ActionsDisclosure
      data-panel="graph_preview_panel"
      title={t("graph.title.expression")}
      disclosureProps={{ defaultOpen: true }}
      className={twMerge("w-full", hideGraph && "hidden")}
      disclosureButtonProps={{
        disabled: hideGraph,
      }}
    >
      <GraphPreviewSelection doc={doc} expressions={expressions} />
      <Graph
        ref={ref}
        doc={doc}
        expression={expression}
        expressions={expressions}
        api={api}
        onInsert={() => trackEvent("graph_insert", { source: "expression" })}
        hidden={hideGraph}
      />
    </ActionsDisclosure>
  );
});

export const GraphPreviewPanel = observer(function GraphPreviewPanel({
  doc,
}: {
  doc: DocumentModel;
}) {
  const selectedExpressions = getSelectedExpressions(doc, EXPRESSION_COUNT_LIMIT);
  // always render after first render, preventing graph re-initialization
  const [status, setStatus] = useState<"init" | "hidden" | "visible">("init");

  useEffect(() => {
    setStatus(canGraphExpressions(selectedExpressions) ? "visible" : "hidden");
  }, [selectedExpressions]);

  if (status === "init") {
    return null;
  }

  return (
    <GraphPreviewPanelComponent
      doc={doc}
      expressions={selectedExpressions}
      hidden={status === "hidden"}
    />
  );
});
